import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/admin/help/RichTextEditor";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Send, Mail, X } from "lucide-react";

type RecipientMode = "all" | "user_type" | "cohort" | "specific_users";
type AudienceRole = "entrepreneur" | "mentor" | "admin" | "instructor";

const ROLE_OPTIONS: { value: AudienceRole; label: string }[] = [
  { value: "entrepreneur", label: "Entrepreneurs" },
  { value: "mentor", label: "Mentors" },
  { value: "admin", label: "Admins" },
  { value: "instructor", label: "Instructors" },
];

interface UserOption { id: string; full_name: string | null }

export function BulkEmailManager() {
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [mode, setMode] = useState<RecipientMode>("all");
  const [roles, setRoles] = useState<AudienceRole[]>([]);
  const [cohortId, setCohortId] = useState<string>("");
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [userSearch, setUserSearch] = useState("");

  const { data: cohorts } = useQuery({
    queryKey: ["bulk-email-cohorts-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cohorts")
        .select("id, name")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: searchResults } = useQuery({
    queryKey: ["bulk-email-user-search", userSearch],
    queryFn: async (): Promise<UserOption[]> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name")
        .ilike("full_name", `%${userSearch.trim()}%`)
        .order("full_name")
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
    enabled: mode === "specific_users" && userSearch.trim().length >= 2,
  });

  // Recipient count estimate (client-side)
  const { data: estimatedCount } = useQuery({
    queryKey: ["bulk-email-estimate", mode, roles, cohortId, selectedUsers.map((u) => u.id)],
    queryFn: async (): Promise<number> => {
      const ids = new Set<string>();
      if (mode === "all") {
        const [{ data: ps }, { data: rs }] = await Promise.all([
          supabase.from("profiles").select("id"),
          supabase.from("user_roles").select("user_id"),
        ]);
        ps?.forEach((p) => ids.add(p.id));
        rs?.forEach((r) => ids.add(r.user_id));
      } else if (mode === "user_type") {
        const profileRoles = roles.filter((r) => ["entrepreneur", "mentor"].includes(r));
        const appRoles = roles.filter((r) => ["admin", "instructor"].includes(r));
        if (profileRoles.length) {
          const { data } = await supabase.from("profiles").select("id").in("role", profileRoles as any);
          data?.forEach((p) => ids.add(p.id));
        }
        if (appRoles.length) {
          const { data } = await supabase.from("user_roles").select("user_id").in("role", appRoles as any);
          data?.forEach((r) => ids.add(r.user_id));
        }
      } else if (mode === "cohort" && cohortId) {
        const { data } = await supabase.from("course_enrollments").select("user_id").eq("cohort_id", cohortId);
        data?.forEach((e) => ids.add(e.user_id));
      } else if (mode === "specific_users") {
        selectedUsers.forEach((u) => ids.add(u.id));
      }
      if (ids.size === 0) return 0;
      // Filter by email pref (skip for specific_users)
      const idArr = Array.from(ids);
      const { data: emails } = await supabase
        .from("profile_private")
        .select("user_id, email")
        .in("user_id", idArr);
      const withEmail = emails?.filter((e) => !!e.email) ?? [];
      if (mode === "specific_users") return new Set(withEmail.map((e) => e.email)).size;
      const { data: prefs } = await supabase
        .from("user_email_preferences")
        .select("user_id, notify_announcements")
        .in("user_id", idArr);
      const optedOut = new Set(
        (prefs ?? []).filter((p) => p.notify_announcements === false).map((p) => p.user_id),
      );
      const finalEmails = new Set(
        withEmail.filter((e) => !optedOut.has(e.user_id)).map((e) => e.email),
      );
      return finalEmails.size;
    },
  });

  const { data: log } = useQuery({
    queryKey: ["bulk-email-log"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bulk_email_log")
        .select("*")
        .order("sent_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
  });

  const senderIds = useMemo(
    () => Array.from(new Set((log ?? []).map((r: any) => r.sent_by).filter(Boolean))),
    [log],
  );

  const { data: senders } = useQuery({
    queryKey: ["bulk-email-senders", senderIds],
    queryFn: async () => {
      if (senderIds.length === 0) return [] as { id: string; full_name: string | null }[];
      const { data, error } = await supabase.from("profiles").select("id, full_name").in("id", senderIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: senderIds.length > 0,
  });

  const senderMap = useMemo(() => {
    const m = new Map<string, string>();
    senders?.forEach((s) => m.set(s.id, s.full_name ?? "Unknown"));
    return m;
  }, [senders]);

  const sendMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { subject, body, recipient_mode: mode };
      if (mode === "user_type") payload.roles = roles;
      if (mode === "cohort") payload.cohort_id = cohortId;
      if (mode === "specific_users") payload.user_ids = selectedUsers.map((u) => u.id);
      const { data, error } = await supabase.functions.invoke("admin-bulk-email", { body: payload });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      return data as { emailsSent: number };
    },
    onSuccess: (data) => {
      toast.success(`Email sent to ${data.emailsSent} recipient(s)`);
      setSubject(""); setBody(""); setRoles([]); setCohortId("");
      setSelectedUsers([]); setUserSearch("");
      queryClient.invalidateQueries({ queryKey: ["bulk-email-log"] });
    },
    onError: (e: Error) => toast.error(`Failed: ${e.message}`),
  });

  const hasTarget =
    (mode === "all") ||
    (mode === "user_type" && roles.length > 0) ||
    (mode === "cohort" && !!cohortId) ||
    (mode === "specific_users" && selectedUsers.length > 0);

  const bodyTextLength = body.replace(/<[^>]*>/g, "").trim().length;
  const canSend = subject.trim().length > 0 && bodyTextLength > 0 && hasTarget;

  const recipientSummary =
    mode === "all"
      ? "All Users"
      : mode === "user_type"
        ? roles.length
          ? roles.map((r) => ROLE_OPTIONS.find((o) => o.value === r)?.label ?? r).join(" + ")
          : "No user types selected"
        : mode === "cohort"
          ? cohortId
            ? `Cohort: ${cohorts?.find((c) => c.id === cohortId)?.name ?? cohortId}`
            : "No cohort selected"
          : `${selectedUsers.length} specific user(s)`;

  const toggleRole = (r: AudienceRole) =>
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const addUser = (u: UserOption) => {
    if (!selectedUsers.find((s) => s.id === u.id)) setSelectedUsers((p) => [...p, u]);
  };
  const removeUser = (id: string) => setSelectedUsers((p) => p.filter((u) => u.id !== id));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Bulk Email
          </CardTitle>
          <CardDescription>
            Send a one-off email to selected users. Recipients with announcement emails disabled will be skipped (except in Specific Users mode).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Recipient Mode</Label>
            <RadioGroup value={mode} onValueChange={(v) => setMode(v as RecipientMode)} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { v: "all", l: "All Users" },
                { v: "user_type", l: "By User Type" },
                { v: "cohort", l: "By Cohort" },
                { v: "specific_users", l: "Specific Users" },
              ].map((opt) => (
                <label key={opt.v} className="flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer hover:bg-muted/40">
                  <RadioGroupItem value={opt.v} id={`mode-${opt.v}`} />
                  <span className="text-sm">{opt.l}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          {mode === "user_type" && (
            <div className="flex flex-wrap gap-3">
              {ROLE_OPTIONS.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={roles.includes(r.value)} onCheckedChange={() => toggleRole(r.value)} />
                  {r.label}
                </label>
              ))}
            </div>
          )}

          {mode === "cohort" && (
            <Select value={cohortId} onValueChange={setCohortId}>
              <SelectTrigger><SelectValue placeholder="Select an active cohort" /></SelectTrigger>
              <SelectContent>
                {cohorts?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
                {cohorts?.length === 0 && (
                  <div className="p-2 text-sm text-muted-foreground">No active cohorts</div>
                )}
              </SelectContent>
            </Select>
          )}

          {mode === "specific_users" && (
            <div className="space-y-2">
              <Input
                placeholder="Search users by name (min 2 characters)…"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
              {userSearch.trim().length >= 2 && (
                <div className="max-h-56 overflow-y-auto border rounded-md divide-y">
                  {searchResults?.map((u) => {
                    const picked = !!selectedUsers.find((s) => s.id === u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => addUser(u)}
                        disabled={picked}
                        className="w-full text-left p-2 text-sm hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {u.full_name || "(no name)"}
                      </button>
                    );
                  })}
                  {searchResults?.length === 0 && (
                    <div className="p-3 text-sm text-muted-foreground">No matches</div>
                  )}
                </div>
              )}
              {selectedUsers.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {selectedUsers.map((u) => (
                    <Badge key={u.id} variant="secondary" className="gap-1">
                      {u.full_name || "(no name)"}
                      <button type="button" onClick={() => removeUser(u.id)} className="ml-1">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {hasTarget && (
            <div className="text-sm text-muted-foreground">
              Estimated recipients: ~{estimatedCount ?? "…"}
            </div>
          )}

          <div className="space-y-2">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" />
          </div>

          <div className="space-y-2">
            <Label>Body</Label>
            <RichTextEditor
              content={body}
              onChange={setBody}
              placeholder="Write your email message..."
              minHeight="300px"
            />
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={!canSend || sendMutation.isPending}>
                {sendMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Send Email
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Send this email?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm">
                    <div><strong>Subject:</strong> {subject}</div>
                    <div><strong>Recipients:</strong> {recipientSummary}</div>
                    <div>This will send an email to approximately {estimatedCount ?? "…"} recipient(s).</div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => sendMutation.mutate()}>Confirm Send</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Send History</CardTitle>
        </CardHeader>
        <CardContent>
          {!log || log.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-muted-foreground">
              <Mail className="h-10 w-10 mb-2 opacity-40" />
              <p>No bulk emails sent yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Sent By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {log.map((row: any) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap">{format(new Date(row.sent_at), "MMM d, yyyy h:mm a")}</TableCell>
                    <TableCell className="max-w-xs truncate">{row.subject}</TableCell>
                    <TableCell>{row.recipient_count}</TableCell>
                    <TableCell><Badge variant="secondary">{row.recipient_mode}</Badge></TableCell>
                    <TableCell>{senderMap.get(row.sent_by) ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
